import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { useQueryClient } from '@tanstack/react-query';

import { ActivityInputValues, defaultActivityInputValues } from 'components/shared/activityInput/useActivityInput';
import {
  AdmissionsCriteriaColor,
  CriteriaCategory,
  CriteriaEvidence,
  CriteriaSourceBadge,
  groupCriteria,
} from 'components/shared/ai/AdmissionsCriteria';
import Foldout from 'components/shared/Foldout';
import Tabs, { TabType } from 'components/Tabs';
import { useProfile } from 'context/profile';
import { useWebsocketSubscription } from 'hooks/useWebsocketSubscription';
import { simpleDateAtTime } from 'lib/date';
import Workflow, { ActionStatus, WorkflowAction } from 'models/Workflow';
import { WorkflowActionIntent, workflowQueryKeys } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';
import ChevronRightBold from 'svg/ChevronRightBold';
import NoteIcon from 'svg/NoteIcon';

import { buildingName, buildingTypeNoun, sortedByBuildingName } from '../FanoutPanel';
import { categoryStatus } from '../referralProgress';
import StatusIcon from '../StatusIcon';

import NotesTab from './notes/NotesTab';
import { readClinicalReviewResult } from './clinicalReviewData';

const slug = (label: string) => label.toLowerCase().replace(/\s+/g, '-');

// The drawer's top-level tabs. The criteria category filter inside the first tab
// is a second, lower layer — pill buttons, not these underline tabs.
const DRAWER_TABS: TabType[] = [
  { label: 'Clinical Assessment', value: 'assessment' },
  { label: 'Notes', value: 'notes' },
];

/**
 * The deep link a mention email carries: `?actionId=…&tab=notes&activityId=…` on the referral
 * route. Query params rather than path segments, following `?locationEpisodeId=` on the patient
 * page — the drawer's open state stays local instead of being promoted to routed state.
 */
const DEEP_LINK_PARAMS = ['actionId', 'tab', 'activityId'];

// Scroll target for a deep-linked row, so a referral with a dozen buildings does not open a
// drawer over an off-screen row.
const reviewRowId = (actionId: string) => `clinical-review-${actionId}`;

// NOTE: requires the API attachment allowlist to include Workflow (HANDOFF-E) — source
// badges render now and become live (openable) once that lands.
const WORKFLOW_ATTACHABLE_MODEL = 'Workflow';

// Figma-matched category styling for the clinical criteria (drawer cards + row count pills):
// a faint category tint (`*5`) behind the criteria label, and a dark, readable tone (`*ExtraDark`)
// for the name/pill text. The bright shared accents (colorConfig[*].accent) fail contrast on white
// and on their own faint tints, so both tones come from the Figma "Secondary" palette. (The `blue`
// category maps to the palette's "lightBlue".)
//
// The tint is 5%, matching the design (`rgba(217,63,76,0.05)` on the red card) and the shared
// `colorConfig[*].background` the admissions cards already use — it was `*10` here, which read as
// twice the intended wash.
type CriteriaStyle = { tint: string; text: string };
const CRITERIA_STYLE: Record<AdmissionsCriteriaColor, CriteriaStyle> = {
  red: { tint: colors.red5, text: colors.redExtraDark },
  yellow: { tint: colors.yellow5, text: colors.yellowExtraDark },
  green: { tint: colors.green5, text: colors.greenExtraDark },
  orange: { tint: colors.orange5, text: colors.orangeExtraDark },
  blue: { tint: colors.lightBlue5, text: colors.lightBlueExtraDark },
};

// The row-level "N Red Criteria" pills read one shade darker than the drawer card header above
// (10% vs. CRITERIA_STYLE's 5%) — a pill has to hold its own color on a white row background,
// where the header tint sits directly above a matching-color body.
const CRITERIA_PILL_TINT: Record<AdmissionsCriteriaColor, string> = {
  red: colors.red10,
  yellow: colors.yellow10,
  green: colors.green10,
  orange: colors.orange10,
  blue: colors.lightBlue10,
};

// The API's "last viewed" attribution (nullable) — the reviewed name + formatted timestamp,
// or null when the building's results have never been viewed. Drives the row's "Viewed by …"
// line and the drawer header's "Last Viewed: …" subtitle.
function lastViewed(action: WorkflowAction): { name: string | null; at: string | null } | null {
  if (action.viewsCount <= 0) return null;
  return { name: action.lastViewedByName, at: action.lastViewedAt ? simpleDateAtTime(action.lastViewedAt) : null };
}

// Matched criteria grouped by category via the shared `groupCriteria` — the same grouping + color
// validation the admission criteria drawer uses. `readClinicalReviewResult` has already sorted by
// category `order`, then match strength, then criteria `order`, so `groupCriteria` (which preserves
// first-appearance order) yields categories and criteria in that explicit display order.
//
// No matched-ness filter here: the api publishes only the matched criteria on
// `clinical_review_result`, so every entry is already matched — the same rule
// `admissions/helpers/criteria.ts` follows for the AIJob payload. An empty result IS the
// "No Criteria Found" state, and the count pills, the View affordance, the drawer's filter tabs and
// its body all derive from this function's output.
function matchedCategoriesFor(workflow: Workflow, action: WorkflowAction): CriteriaCategory[] {
  return groupCriteria(
    readClinicalReviewResult(workflow, action).map((c) => ({
      categoryId: c.category?.id,
      categoryName: c.category?.name ?? 'Uncategorized',
      categoryColor: c.category?.color,
      criteria: {
        label: c.label,
        description: c.description || undefined,
        finding: c.finding || undefined,
        matchStrength: c.matchStrength || undefined,
        sourceCount: c.sources.length,
        sources: c.sources.map((s) => ({ attachmentId: s.attachmentId, displayId: s.displayId })),
      },
    }))
  );
}

// The criteria a building can show, or null when there is no settled result to read — still
// processing, failed, or not yet reached. Lifted out of the row so the fan-out count below reads
// the same rule the row does: an empty array here is the "No Criteria Found" state, which is a
// building with a result and nothing in it.
function readableCategories(workflow: Workflow, action: WorkflowAction): CriteriaCategory[] | null {
  const status = action.effectiveStatus;
  if (status === ActionStatus.RUNNING || status === ActionStatus.ERRORED) return null;
  return reviewSettled(action) ? matchedCategoriesFor(workflow, action) : null;
}

// Body copy for the states that list NO criteria (OLIO-3193). These used to fall through to the
// definition's own description — "Review the clinical admission criteria below…" — which told a
// reviewer to review criteria that were not there, and gave no hint whether the emptiness was a
// failure, a wait, or simply nothing to do. Each one now says why the list is empty and what, if
// anything, happens next.
//
// Keyed on the roll-up categoryStatus produces, which is also what the card's status badge reads —
// so the sentence and the badge are always describing the same state — and, since OLIO-3270, in the
// same words: `skipped` badges as "Not Applicable", which is what the design called the state all
// along.
//
// TOTAL over ActionStatus on purpose, not Partial: a new member is then a compile error here rather
// than a silent fallthrough to the definition's copy. That fallthrough is exactly how `errored`
// shipped reading "review the criteria below" over a failed assessment — the gap was invisible
// because the type permitted it.
//
// The settled states (action_required / completed) carry a sentence too, because "settled" does not
// mean "found something": an assessment can complete having matched nothing. Their entries only ever
// render in that case — `anyCriteriaShown` returns the definition's copy first whenever any building
// still has criteria on screen, which is the only situation in which "review the criteria below" is
// a true statement.
const STATE_BLURBS: Record<ActionStatus, string> = {
  // Flagged — the referral's documents failed to process, so nothing could be assessed.
  [ActionStatus.FLAGGED]:
    'Olio couldn’t process the documents attached to this referral, so no criteria were identified.',
  // Pending — documents are still processing; the assessment fills in on its own.
  [ActionStatus.PENDING]:
    'The documents for this referral are still processing. Admission criteria will appear here automatically once they’re ready.',
  // Running — the documents are done and the assessment ITSELF is generating. Distinct from
  // pending, which is still waiting on the documents, so the two states name different waits.
  [ActionStatus.RUNNING]:
    'This assessment is still being generated. Admission criteria will appear here automatically once it’s complete.',
  // Not Applicable — no documents were ever shared, so there was nothing to assess. Reversible:
  // adding documents later re-opens the step, which is why the copy promises it.
  [ActionStatus.SKIPPED]:
    'No documents have been shared for this referral, so there are no admission criteria to review. Criteria will be identified automatically if documents are added later.',
  // Errored — the assessment itself failed to generate. Distinct from flagged, which is the
  // DOCUMENTS failing upstream; here the documents were fine and our own run broke. The errored
  // rows each carry a Retry link, which is what "below" points at.
  [ActionStatus.ERRORED]:
    'Olio ran into a problem generating this assessment, so no criteria were identified. You can retry it below.',
  // Canceled — the work was stopped and is not coming back, so unlike every other state here the
  // copy promises nothing further.
  [ActionStatus.CANCELED]: 'This assessment was canceled, so no admission criteria were identified.',
  // Settled with nothing found. Reached only when no building has criteria (see above), so this is
  // the "assessment ran and matched nothing" card, not one whose criteria are simply listed below.
  // Ready for Review and Complete say the same thing here: what the reader needs is that the run
  // happened and came back empty, which is a different fact from it never having run.
  [ActionStatus.ACTION_REQUIRED]:
    'Olio reviewed the documents for this referral and didn’t identify any admission criteria to flag.',
  [ActionStatus.COMPLETED]:
    'Olio reviewed the documents for this referral and didn’t identify any admission criteria to flag.',
};

// The blurb above the building list: the state's own copy where it has one, else the definition's
// description as before.
//
// `anyCriteriaShown` is the guard against a blurb contradicting the rows underneath it. The blurb is
// ONE sentence over a roll-up; the criteria are PER BUILDING. categoryStatus ranks errored first, so
// two SNFs where one assessment failed and the other completed with matches would otherwise print
// "…so no criteria were identified" directly above a row listing them. Every sentence in
// STATE_BLURBS asserts an absence, so none of them may render while any building still has criteria
// on screen — that case returns the definition's copy, which is true again precisely because there
// IS something to review. That same guard is what lets the settled states carry a sentence at all.
//
// The gate is "no building has criteria", not "no building has a settled result". A building that
// settled with an EMPTY result has a result but nothing to show, and it must not drag the card back
// to "review the criteria below" over an empty list.
//
// A stale step badges Flagged too, but its flag means "inputs changed, re-run me" — not a document
// failure — so telling the reader Olio couldn't process the documents would be false. Stale keeps the
// definition's copy and lets the stale banner speak for itself.
//
// That carve-out is scoped to the case it describes: the roll-up reads Flagged AND nothing is
// actually flagged, which is the only way stale can be what produced it (categoryStatus flags on
// `effectiveStatus === FLAGGED || stale`). A blanket "any building is stale" check was card-wide, so
// a stale building beside an ERRORED one — a roll-up stale had no part in, since errored outranks
// flagged — silently lost the errored copy. It also swallowed the reverse case, a genuinely flagged
// building beside a stale one, where the document failure is real and worth saying.
function reviewBlurb(actions: WorkflowAction[], anyCriteriaShown: boolean): string | undefined {
  const defined = actions[0]?.description ?? undefined;
  if (anyCriteriaShown) return defined;

  const rollup = categoryStatus(actions);
  const flaggedOnlyByStale =
    rollup === ActionStatus.FLAGGED && !actions.some((action) => action.effectiveStatus === ActionStatus.FLAGGED);
  if (flaggedOnlyByStale) return defined;

  return STATE_BLURBS[rollup];
}

// The clinical review CONTENTS (Figma #8/#9/#10): a short blurb, an "SNFs (N)" count, then
// one clean row per building — no per-row status icon and no divider chrome (the shared
// FanoutPanel's icons/borders read as clutter here). Each row is name + a status line
// (matched-criteria pills, "No Criteria Found", "Processing…", or "Failed to process") with
// a View/Retry affordance on the right.
export default function ClinicalReviewPanel({
  actions,
  workflow,
  onInvoke,
  isInvoking,
  onView,
}: {
  actions: WorkflowAction[];
  workflow: Workflow;
  onInvoke: (id: string, intent: WorkflowActionIntent) => void;
  isInvoking: boolean;
  onView: (actionId: string) => void;
}) {
  const ordered = sortedByBuildingName(actions);
  // The buildings a fanned-out note can actually reach. The API targets only assessments that
  // settled WITH criteria — a reader opens a building's thread off its result, so one that is
  // unassessed, still processing, failed, or found nothing has no way in and receives no copy.
  // Counting every sibling instead would offer the checkbox where the note has nowhere else to go,
  // and promise a footer count the reader could never open.
  //
  // It doubles as the blurb's guard: "some building has criteria on screen" is the same fact, so the
  // sentence above the list and the note fan-out below it are reading one number, not two rules that
  // could drift apart.
  const reachableCount = ordered.filter((action) => (readableCategories(workflow, action) ?? []).length > 0).length;
  const blurb = reviewBlurb(ordered, reachableCount > 0);

  return (
    <ReviewBody data-testid='fanout-panel'>
      {blurb && <ReviewBlurb>{blurb}</ReviewBlurb>}
      <div>
        {/* A lone building needs no count heading — the single row speaks for itself. */}
        {ordered.length > 1 && (
          <ReviewCount>
            {/* "SNFs (4)" — the buildings' shared type noun, capitalized as a heading; a mixed or
                untyped set falls back to "Facilities". Same all-agree rule as the header caption. */}
            {buildingTypeNoun(ordered, 'Facilities')} ({ordered.length})
          </ReviewCount>
        )}
        <ReviewList $scroll={ordered.length > 4}>
          {ordered.map((action) => {
            // Presentation reads effectiveStatus (the API-derived view of status), per the
            // convention effectiveStatus introduced — ClinicalReview doesn't diverge it today, so
            // this matches status, but keeps one rule for what the FE renders.
            const running = action.effectiveStatus === ActionStatus.RUNNING;
            const errored = action.effectiveStatus === ActionStatus.ERRORED;
            const viewed = lastViewed(action);
            // Matched criteria for a settled building — grouped once here and shared by the
            // status line, the View affordance, and the drawer (null while in-flight/failed).
            const matchedCategories = readableCategories(workflow, action);
            return (
              // One row layout for every state (Figma node 10610): an optional leading status
              // icon (spinner / amber warning) for in-flight and failed buildings, the name +
              // state stacked in the middle, and a right-aligned action (View / Retry) — all
              // vertically centered against each other. Settled buildings have no icon, so
              // their names sit flush-left.
              <ReviewRow
                key={action.id}
                id={reviewRowId(action.id)}
                data-testid='fanout-row'
                data-group-id={action.groupId ?? undefined}>
                {(running || errored) && (
                  <StatusIconCell>
                    <StatusIcon indicator={running ? 'running' : 'error'} size={14} />
                  </StatusIconCell>
                )}
                <ReviewLeadMain $tight={running || errored} data-testid='review-lead'>
                  <ReviewName>{buildingName(action)}</ReviewName>
                  {running && <StatusLine $color={colors.purple100}>Processing…</StatusLine>}
                  {errored && <StatusLine $color={colors.red100}>Error</StatusLine>}
                  {matchedCategories && <ReviewStatus matchedCategories={matchedCategories} />}
                  {viewed && (
                    <ViewedLine>
                      Viewed{viewed.name ? ` by ${viewed.name}` : ''}
                      {viewed.at ? ` on ${viewed.at}` : ''}
                    </ViewedLine>
                  )}
                </ReviewLeadMain>
                {errored ? (
                  <RetryLink
                    type='button'
                    data-testid='review-retry'
                    disabled={isInvoking}
                    onClick={() => onInvoke(action.id, 'rerun')}>
                    Retry
                  </RetryLink>
                ) : matchedCategories && matchedCategories.length > 0 ? (
                  // Matched criteria to show → a View link into the drawer. "No Criteria
                  // Found" (empty matchedCategories) gets no affordance.
                  <ReviewView
                    action={action}
                    workflow={workflow}
                    matchedCategories={matchedCategories}
                    // The exact count, not an estimate: the same set the server resolves as the
                    // fan-out's targets, arrived at from the same pair of facts (settled, and
                    // carrying criteria) that its `accepts_fanned_out_notes?` reads.
                    groupCount={reachableCount}
                    onView={onView}
                  />
                ) : null}
              </ReviewRow>
            );
          })}
        </ReviewList>
      </div>
    </ReviewBody>
  );
}

// Whether the AI has settled a building's result — matches found (action_required, awaiting
// the coordinator's review) or nothing to review (completed: no matches, or already reviewed).
// Until then (running / errored / pending) there's no criteria outcome to show.
function reviewSettled(action: WorkflowAction): boolean {
  return action.isActionRequired || action.isCompleted;
}

// The settled row's second line: the per-building criteria outcome. (Processing and failed
// states have their own leading-icon layout in the panel.)
function ReviewStatus({ matchedCategories }: { matchedCategories: CriteriaCategory[] }) {
  if (matchedCategories.length === 0) return <StatusLine $color={colors.black50}>No Criteria Found</StatusLine>;
  return (
    <CountRow>
      {matchedCategories.map((cat) => (
        <CriteriaCountPill
          key={cat.id ?? cat.label}
          $bg={CRITERIA_PILL_TINT[cat.color]}
          $text={CRITERIA_STYLE[cat.color].text}
          data-testid={`criteria-count-${slug(cat.label)}`}>
          {cat.criteria.length} {cat.label} Criteria
        </CriteriaCountPill>
      ))}
    </CountRow>
  );
}

function ReviewView({
  action,
  workflow,
  matchedCategories,
  groupCount,
  onView,
}: {
  action: WorkflowAction;
  workflow: Workflow;
  matchedCategories: CriteriaCategory[];
  groupCount: number;
  onView: (actionId: string) => void;
}) {
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * A mention email lands here. `actionId` is compared against this row's action, so exactly one
   * drawer opens — and an id that matches nothing opens none, which is what a re-run between the
   * email and the click looks like: the show payload renders only current actions, so the reader
   * is simply on the right referral with nothing open. Degraded, never an error.
   */
  const deepLinked = searchParams.get('actionId') === action.id;

  /**
   * The arrival is spent once the note has been reached. Held here rather than in NotesTab, and not
   * read off the URL: the params stay put until the drawer closes, and the tab unmounts every time
   * the reader looks at the assessment — so the tab cannot remember this for itself, and coming
   * back would page the thread forward again and yank the scroll to a note already read.
   */
  const [focusSpent, setFocusSpent] = useState(false);
  const focusActivityId = deepLinked && !focusSpent ? searchParams.get('activityId') : null;

  const [open, setOpen] = useState(deepLinked);
  // selectedTab lives here rather than in ReviewDrawer so the subscription below can tell
  // whether this action's Notes tab is on screen.
  //
  // Out here it also survives a close, so reopening this building lands on the tab it was left
  // on — deliberate, and the same call as the draft below. Resetting to the assessment while
  // keeping the draft would hide a half-typed note behind a tab the reader has to find again.
  // Scoped per building, so a different row still opens on the assessment.
  const [selectedTab, setSelectedTab] = useState<TabType>(
    (deepLinked && DRAWER_TABS.find((tab) => tab.value === searchParams.get('tab'))) || DRAWER_TABS[0]
  );
  // The notes composer's draft lives out here rather than in NotesTab. The drawer swaps tabs by
  // conditional render, and Foldout unmounts its children when closed, so anything held further
  // in is destroyed by a glance at the assessment tab or a mis-click on the backdrop. Out here it
  // survives both, and stays scoped to this building.
  const [noteDraft, setNoteDraft] = useState(defaultActivityInputValues);
  // Lifted for the same reason the draft is, and kept out of ActivityInputValues because that type
  // is shared with the patient story, where "all groups" means nothing.
  const [sendToAll, setSendToAll] = useState(false);
  const { profile } = useProfile();
  const queryClient = useQueryClient();

  const notesOpen = open && selectedTab.value === 'notes';

  /**
   * The row icon renders with the slideout shut, so this subscription has to outlive the
   * drawer — unlike NotesTab's, which is scoped to that tab's mount. useWebsocketSubscription
   * ref-counts by serialized identifier, so when both are live they share one ActionCable
   * subscription and each handler is called.
   *
   * One subscription per rendered clinical review, because every channel in this codebase is
   * keyed to a single record id. That is one per facility under consideration — single digits.
   */
  useWebsocketSubscription<{ id: string; activities?: boolean; actorId: string | null }>({
    channel: 'WorkflowActionChannel',
    params: { workflow_action_id: action.id },
    onReceived: (data) => {
      // Your own notes never count as unread.
      if (data.actorId === profile?.id) return;
      // The subscribe-time transmit is { id, actorId } with no association key.
      if (!data.activities) return;
      // The note is already on screen, and NotesTab's invalidation refetches page 1 — which
      // stamps the arriving note read. Flipping the flag here would light an icon that is wrong
      // the instant it appears.
      if (notesOpen) return;

      queryClient.setQueryData<Workflow>(
        workflowQueryKeys.show(workflow.id),
        (prev) => prev?.withUnreadActivities(action.id) ?? prev
      );
    },
  });

  // Opening the drawer marks the building's results viewed (POSTs a view; the refetch updates
  // the "Viewed by" attribution).
  const openDrawer = () => {
    onView(action.id);
    setOpen(true);
  };

  /**
   * Arriving by deep link: bring the row on screen behind the drawer, and record the view exactly
   * as openDrawer does. The clinical review is open either way, and how it was opened should not
   * change what "open" means for its lifecycle.
   *
   * Mount only — arrival is a one-time event, and re-running when the params clear would re-record
   * a view for a drawer the reader has just shut. The ref survives StrictMode's double invocation,
   * which would otherwise record every development arrival twice.
   */
  const viewRecorded = useRef(false);
  useEffect(() => {
    if (!deepLinked || viewRecorded.current) return;
    viewRecorded.current = true;

    onView(action.id);
    document.getElementById(reviewRowId(action.id))?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Closing clears the params, or the next render reopens what was just dismissed. Watches `open`
   * rather than wrapping one handler, so both ways out — the X and the backdrop — are covered.
   */
  useEffect(() => {
    if (open || !deepLinked) return;

    const next = new URLSearchParams(searchParams);
    DEEP_LINK_PARAMS.forEach((key) => next.delete(key));
    setSearchParams(next, { replace: true });
  }, [open, deepLinked, searchParams, setSearchParams]);

  return (
    <>
      <ViewAffordance>
        {/* Immediately left of "View", on the same baseline. Rows with nothing unread render
            the link alone, with no reserved gap. */}
        {action.hasNewActivities && (
          <NoteIcon color={colors.blue100} width={12} height={13} data-testid='review-unread-notes' />
        )}
        <ViewLink type='button' onClick={openDrawer} data-testid='review-view'>
          View
          <ChevronRightBold width={6} height={10} />
        </ViewLink>
      </ViewAffordance>
      {/* Wide panel per the Figma (#9/#12) — the criteria cards need the room; the default
          Foldout width (360px) reads as a cramped strip. */}
      <Foldout open={open} setOpen={setOpen} closeOnBackdropClick width='min(92%, 960px)' data-testid='review-slideout'>
        <ReviewDrawer
          action={action}
          workflow={workflow}
          matchedCategories={matchedCategories}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          noteDraft={noteDraft}
          setNoteDraft={setNoteDraft}
          groupCount={groupCount}
          sendToAll={sendToAll}
          setSendToAll={setSendToAll}
          focusActivityId={focusActivityId}
          onFocusHandled={() => setFocusSpent(true)}
          onClose={() => setOpen(false)}
        />
      </Foldout>
    </>
  );
}

function ReviewDrawer({
  action,
  workflow,
  matchedCategories,
  selectedTab,
  setSelectedTab,
  noteDraft,
  setNoteDraft,
  groupCount,
  sendToAll,
  setSendToAll,
  focusActivityId,
  onFocusHandled,
  onClose,
}: {
  action: WorkflowAction;
  workflow: Workflow;
  matchedCategories: CriteriaCategory[];
  // Owned by ReviewView so the row's channel handler can see which tab is open.
  selectedTab: TabType;
  setSelectedTab: (tab: TabType) => void;
  noteDraft: ActivityInputValues;
  setNoteDraft: Dispatch<SetStateAction<ActivityInputValues>>;
  groupCount: number;
  sendToAll: boolean;
  setSendToAll: Dispatch<SetStateAction<boolean>>;
  focusActivityId?: string | null;
  onFocusHandled: () => void;
  onClose: () => void;
}) {
  const viewed = lastViewed(action);

  // The blue note icon rides the tab's affix slot, so it stays visible from the Assessment tab.
  // Same mark and same boolean as the row icon — one flag, two surfaces. NoteIcon is the bare
  // glyph; ActivityNoteIcon is the same mark inside a tinted badge, which is the patient-story
  // activity-row treatment and not what this indicator wants.
  const tabs = DRAWER_TABS.map((tab) =>
    tab.value === 'notes' && action.hasNewActivities
      ? {
          ...tab,
          affix: <NoteIcon color={colors.blue100} width={12} height={13} data-testid='notes-tab-unread' />,
        }
      : tab
  );

  return (
    <SlideoutBody>
      <DrawerHeader>
        <div>
          <DrawerTitle>Clinical Assessment &ndash; {buildingName(action)}</DrawerTitle>
          {viewed && (
            <DrawerSubtitle>
              Last Viewed:{viewed.name ? ` ${viewed.name}` : ''}
              {viewed.at ? ` at ${viewed.at}` : ''}
            </DrawerSubtitle>
          )}
        </div>
        <CloseButton type='button' onClick={onClose} aria-label='Close' data-testid='review-close'>
          &times;
        </CloseButton>
      </DrawerHeader>

      <DrawerTabs>
        <Tabs tabs={tabs} onTabClick={setSelectedTab} selectedTab={selectedTab} />
      </DrawerTabs>

      <TabBody>
        {selectedTab.value === 'assessment' ? (
          // Each tab owns its own overflow: both keep their controls fixed and scroll only the
          // list beneath them.
          <AssessmentTab matchedCategories={matchedCategories} />
        ) : (
          <NotesTab
            workflowId={workflow.id}
            action={action}
            groupCount={groupCount}
            values={noteDraft}
            setValues={setNoteDraft}
            sendToAll={sendToAll}
            setSendToAll={setSendToAll}
            focusActivityId={focusActivityId}
            onFocusHandled={onFocusHandled}
          />
        )}
      </TabBody>
    </SlideoutBody>
  );
}

// The criteria content, unchanged. Its category filter is a second, lower tab layer inside
// the first top-level tab.
function AssessmentTab({ matchedCategories }: { matchedCategories: CriteriaCategory[] }) {
  const total = matchedCategories.reduce((n, c) => n + c.criteria.length, 0);
  const [filter, setFilter] = useState<string>('all'); // 'all' | category label
  const shown = filter === 'all' ? matchedCategories : matchedCategories.filter((c) => c.label === filter);

  return (
    <AssessmentLayout>
      <FilterTabs>
        <FilterTab
          type='button'
          $active={filter === 'all'}
          data-testid='review-filter-all'
          onClick={() => setFilter('all')}>
          All ({total})
        </FilterTab>
        {matchedCategories.map((cat) => (
          <FilterTab
            key={cat.id ?? cat.label}
            type='button'
            $active={filter === cat.label}
            data-testid={`review-filter-${slug(cat.label)}`}
            onClick={() => setFilter(cat.label)}>
            {cat.label} ({cat.criteria.length})
          </FilterTab>
        ))}
      </FilterTabs>
      <DrawerDivider />
      <CriteriaScrollArea data-testid='criteria-list'>
        {shown.map((cat) => (
          <CriteriaGroup key={cat.id ?? cat.label} data-testid={`criteria-group-${slug(cat.label)}`}>
            <GroupHeading>
              {cat.label} Criteria ({cat.criteria.length})
            </GroupHeading>
            {cat.criteria.map((item, i) => (
              <CriteriaCard key={i}>
                <CriteriaHeader $tint={CRITERIA_STYLE[cat.color].tint} $divided={Boolean(item.description)}>
                  <CriteriaLabel $accent={CRITERIA_STYLE[cat.color].text}>{item.label}</CriteriaLabel>
                  {(item.sources ?? []).map((source) => (
                    <CriteriaSourceBadge
                      key={source.displayId}
                      attachmentId={source.attachmentId}
                      displayId={source.displayId}
                      attachmentType={WORKFLOW_ATTACHABLE_MODEL}
                      size={14}
                    />
                  ))}
                </CriteriaHeader>
                {item.description && <CriteriaDescription>{item.description}</CriteriaDescription>}
                {/* The design rules off the evidence from the criteria body above it. When there is no
                    description this divider is the ONLY rule under the header — which is why the header
                    itself is divided on the description alone. Dividing it on the strength too would
                    stack its border against this one into a double line. */}
                {item.matchStrength && (
                  <>
                    <CriteriaDivider />
                    <CriteriaEvidenceRow>
                      <CriteriaEvidence matchStrength={item.matchStrength} finding={item.finding} />
                    </CriteriaEvidenceRow>
                  </>
                )}
              </CriteriaCard>
            ))}
          </CriteriaGroup>
        ))}
      </CriteriaScrollArea>
    </AssessmentLayout>
  );
}

const ReviewBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`;

const ReviewBlurb = styled(BodySmall)`
  font-size: 14px;
  line-height: 18px;
  color: ${colors.black100};
`;

const ReviewCount = styled(BodySmallBold)`
  display: block;
  margin-bottom: 4px;
  font-size: 14px;
  color: ${colors.black100};
`;

// With more than 4 buildings the list scrolls in place (Figma #10) rather than growing the
// card unbounded; the max-height shows ~4 rows so the 5th peeks to signal there's more.
const ReviewList = styled.div<{ $scroll: boolean }>`
  display: flex;
  flex-direction: column;
  ${({ $scroll }) =>
    $scroll &&
    `
      max-height: 296px;
      overflow-y: auto;
    `}
`;

// Rows are separated by a light gray rule (Figma #10). One centered flex layout for all
// states: [optional status icon] [name + state] [right-aligned action]. The icon and action
// stay vertically centered against the stacked name/state in the middle.
const ReviewRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 0;

  & + & {
    border-top: 1px solid ${colors.black10};
  }
`;

// In-flight and failed rows stack their name and state line tighter (4px) than a settled
// row's name-over-criteria (8px), per Figma.
const ReviewLeadMain = styled.div<{ $tight: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $tight }) => ($tight ? '4px' : '8px')};
  min-width: 0;
  flex: 1;
`;

// A fixed-size cell for the leading status icon so the spinner and the error glyph occupy the
// same footprint (and names indent by the same amount).
const StatusIconCell = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
`;

const ReviewName = styled(BodySmallBold)`
  font-size: 14px;
  line-height: 16px;
  color: ${colors.black100};
`;

const StatusLine = styled(BodySmall)<{ $color: string }>`
  font-size: 12px;
  line-height: 16px;
  color: ${({ $color }) => $color};
`;

// The per-row "last viewed" attribution (Figma) — a muted note under a reviewed building's
// criteria (see lastViewed()).
const ViewedLine = styled(BodySmall)`
  font-size: 11px;
  line-height: 16px;
  color: ${colors.black50};
`;

// A bare button reset shared by the row's View / Retry links (Figma text-links: blue, no
// underline).
const LinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: ${colors.blue100};
  cursor: pointer;
`;

// The unread-note icon and the View link sit on one baseline. No gap is reserved when there is
// no icon — the link renders alone, exactly as it did before.
const ViewAffordance = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// The review View link (Figma), with a small chevron after the label. Flex-centering the
// chevron against the descender-less "View" leaves it reading a touch high, so nudge it down
// 1px to optically center on the text.
const ViewLink = styled(LinkButton)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  white-space: nowrap;

  svg {
    transform: translateY(1px);
  }
`;

// The retry action for a failed review (Figma node 10610 — text-link blue, no destructive
// treatment), matching the View affordance.
const RetryLink = styled(LinkButton)`
  font-size: 12px;
  white-space: nowrap;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const CountRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const CriteriaCountPill = styled.span<{ $bg: string; $text: string }>`
  display: inline-flex;
  align-items: center;
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 400;
  line-height: 1;
  white-space: nowrap;
  background: ${({ $bg }) => $bg};
  color: ${({ $text }) => $text};
`;

// Fills the fixed-height Foldout exactly, so the drawer itself never scrolls — the header and
// tab row stay put and each tab owns its own scrolling region. `overflow: hidden` is what stops
// Foldout's own `overflow-y: auto` (Foldout.tsx:82) from taking over and moving the whole panel.
const SlideoutBody = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
  overflow: hidden;
  padding: 24px;
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  flex-shrink: 0;
`;

// The underline tab row sits under the header, spanning edge-to-edge past the body padding.
const DrawerTabs = styled.div`
  border-bottom: 1px solid ${colors.black10};
  margin: 0 -24px 20px;
  padding: 0 24px;
  flex-shrink: 0;
`;

// The remaining space below the tabs. `min-height: 0` is load-bearing: without it a flex child
// refuses to shrink below its content, and the scroll moves back up to the drawer.
const TabBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

// The category filter and the divider under it stay put; only the criteria scroll. Same shape
// as the notes tab, where the composer stays put and only the thread moves.
const AssessmentLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

// `min-height: 0` is what confines the scrollbar here rather than letting it escape upward and
// take the filter row with it.
const CriteriaScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

// Full-bleed past the body's 24px padding. Safe only because this sits OUTSIDE
// CriteriaScrollArea: `overflow-y: auto` forces overflow-x to compute to `auto` too, so inside
// that box these margins would hang 24px past each edge and add a horizontal scrollbar.
const DrawerDivider = styled.hr`
  margin: 16px -24px 20px;
  border: none;
  border-top: 1px solid ${colors.black10};
  flex-shrink: 0;
`;

const DrawerTitle = styled(BodySmallBold)`
  font-size: 16px;
  color: ${colors.black100};
`;

// The drawer's "Last Viewed" attribution under the title (Figma) — small and muted
// (see lastViewed()).
const DrawerSubtitle = styled(BodySmall)`
  margin-top: 4px;
  font-size: 11px;
  color: ${colors.black50};
`;

const CloseButton = styled.button`
  flex-shrink: 0;
  background: none;
  border: none;
  padding: 0;
  font-size: 24px;
  line-height: 1;
  color: ${colors.black50};
  cursor: pointer;

  &:hover {
    color: ${colors.black100};
  }
`;

const FilterTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex-shrink: 0;
`;

// Tab buttons for the drawer's category filter (Figma #12): bordered rounded rects; the
// active tab reads primary-blue (border + light tint + blue text), inactive is a neutral
// gray outline on white.
const FilterTab = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: ${({ $active }) => ($active ? 700 : 400)};
  white-space: nowrap;
  cursor: pointer;
  border: 1px solid ${({ $active }) => ($active ? colors.blue100 : colors.black25)};
  background: ${({ $active }) => ($active ? colors.blue10 : colors.white)};
  color: ${({ $active }) => ($active ? colors.blue100 : colors.black100)};
`;

// A criteria section in the drawer ("Red Criteria (2)" etc.), grouped by category. Cards
// within a group sit 16px apart; groups 28px apart (Figma #12).
const CriteriaGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  & + & {
    margin-top: 28px;
  }
`;

const GroupHeading = styled(BodySmallBold)`
  font-size: 12px;
  color: ${colors.black100};
`;

// A single criteria card (Figma #12): a color-tinted header carrying the criteria label
// (in the category's dark tone) + its supporting-source badges, a gray divider, then a
// white description body. The drawer is built on the 12px label scale, not the 14px body
// scale the workflow card uses.
const CriteriaCard = styled.div`
  border: 1px solid ${colors.black10};
  border-radius: 4px;
  overflow: hidden;
`;

const CriteriaHeader = styled.div<{ $tint: string; $divided: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  background: ${({ $tint }) => $tint};
  border-bottom: ${({ $divided }) => ($divided ? `1px solid ${colors.black10}` : 'none')};
`;

const CriteriaLabel = styled.span<{ $accent: string }>`
  font-size: 12px;
  font-weight: 500;
  color: ${({ $accent }) => $accent};
`;

const CriteriaDescription = styled(BodySmall)`
  padding: 16px;
  font-size: 12px;
  color: ${colors.black100};
  line-height: 20px;
`;

// Holds the shared Match Strength disclosure, inset to match CriteriaDescription so the caret lines
// up under the criteria body text. Only rendered when there is a strength to show, so it needs no
// empty-state collapse.
const CriteriaEvidenceRow = styled.div`
  padding: 14px 16px;
`;

const CriteriaDivider = styled.hr`
  margin: 0;
  border: none;
  border-top: 1px solid ${colors.black10};
`;
